/**
 * variant-system.js
 *
 * Extends product.js with:
 *  - Cascading option availability (disables invalid combos downstream)
 *  - Style 2 card dynamic price & per-unit price updates
 *  - Sticky ATC compare-at price sync
 *
 * Depends on: jQuery, product.js (matchVariant, selectedVarint must already exist)
 */

(function ($) {
  'use strict';

  // ─── Data layer ──────────────────────────────────────────────────────────────

  /**
   * Build an array of variant objects from the hidden select element.
   * Each entry mirrors a Shopify variant with the data we need for UI updates.
   */
  function buildVariantList() {
    var list = [];
    $('.select__variant option').each(function () {
      var $opt = $(this);
      var title = $opt.text().trim();
      var priceRaw = parseInt($opt.attr('data-variant-price-raw'), 10) || 0;
      var compareAtRaw = parseInt($opt.attr('data-variant-compare-at-price-raw'), 10) || 0;
      var rawImageIdx = $opt.attr('data-variant-image-index');
      list.push({
        id: $opt.val(),
        title: title,
        // options[0] = option1 value, options[1] = option2, etc. (0-indexed)
        options: title.split(' / '),
        priceFormatted: $opt.attr('data-variant-price') || '',
        compareAtFormatted: $opt.attr('data-variant-compare-at-price') || '',
        available: $opt.attr('data-variant-available') === 'true',
        priceRaw: priceRaw,
        compareAtRaw: compareAtRaw,
        units: parseInt($opt.attr('data-units'), 10) || 0,
        unitName: $opt.attr('data-unit-name') || '',
        imageIndex: rawImageIdx !== undefined ? parseInt(rawImageIdx, 10) : null
      });
    });
    return list;
  }

  /**
   * Returns currently checked option values keyed by 1-based position.
   * e.g. { 1: '3 multipacks', 2: 'lemon', 3: 'red' }
   */
  function getCurrentSelections() {
    var sel = {};
    $('.product__flavour-item input[type="radio"]:checked').each(function () {
      var pos = parseInt($(this).attr('data-option-position'), 10);
      if (!isNaN(pos)) sel[pos] = $(this).val();
    });
    return sel;
  }

  /**
   * Returns all distinct option positions present on the page, sorted ascending.
   */
  function getPositions() {
    var seen = {};
    $('.product__flavour-item input[type="radio"]').each(function () {
      var pos = parseInt($(this).attr('data-option-position'), 10);
      if (!isNaN(pos)) seen[pos] = true;
    });
    return Object.keys(seen).map(Number).sort(function (a, b) { return a - b; });
  }

  // ─── Money formatting ────────────────────────────────────────────────────────

  var _moneySym = null;

  /**
   * Format an integer pence/cent value using the store's existing currency symbol,
   * derived from the hidden select's first formatted price.
   */
  function formatMoney(pence) {
    if (_moneySym === null) {
      var sample = $('.select__variant option').first().attr('data-variant-price') || '£0.00';
      var m = sample.match(/^([^0-9\s]+)/);
      _moneySym = m ? m[1] : '£';
    }
    var parts = (Math.abs(pence) / 100).toFixed(2).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return _moneySym + parts.join('.');
  }

  // ─── Variant lookup ──────────────────────────────────────────────────────────

  /**
   * Find the first variant whose options match ALL entries in the selections map.
   * Positions not present in selections are treated as wildcards.
   *
   * @param {Array}  variantList
   * @param {Object} selections  { position: value, ... }
   * @param {number} numPositions  total option count (used as upper bound)
   * @returns variant object or null
   */
  function findVariant(variantList, selections, numPositions) {
    for (var i = 0; i < variantList.length; i++) {
      var v = variantList[i];
      var ok = true;
      for (var p = 1; p <= numPositions; p++) {
        if (selections[p] !== undefined && v.options[p - 1] !== selections[p]) {
          ok = false;
          break;
        }
      }
      if (ok) return v;
    }
    return null;
  }

  /**
   * Returns true if at least one *available* variant matches the partial selections.
   */
  function hasAvailableVariant(variantList, selections) {
    for (var i = 0; i < variantList.length; i++) {
      var v = variantList[i];
      if (!v.available) continue;
      var ok = true;
      for (var pos in selections) {
        if (Object.prototype.hasOwnProperty.call(selections, pos)) {
          if (v.options[pos - 1] !== selections[pos]) { ok = false; break; }
        }
      }
      if (ok) return true;
    }
    return false;
  }

  // ─── Cascading availability ──────────────────────────────────────────────────

  /**
   * For every option position after the first, disable inputs whose value cannot
   * combine with the currently selected upstream values to form a real variant.
   *
   * If the currently selected value at a position becomes unavailable, the first
   * available alternative is auto-selected and `currentSelections` is updated in
   * place so the downstream cascade uses the corrected value.
   *
   * @param {Array}  variantList
   * @param {Object} currentSelections  mutated in place when auto-switch occurs
   * @param {Array}  positions          sorted list of option positions
   */
  function cascadeAvailability(variantList, currentSelections, positions) {
    for (var i = 1; i < positions.length; i++) {
      var pos = positions[i];

      // Build upstream snapshot (all positions before this one)
      var upstream = {};
      for (var j = 0; j < i; j++) {
        upstream[positions[j]] = currentSelections[positions[j]];
      }

      var firstAvailVal = null;
      var currentIsAvailable = false;

      $('.product__flavour-item input[type="radio"][data-option-position="' + pos + '"]').each(function () {
        var $input = $(this);
        var val = $input.val();

        var testSel = $.extend({}, upstream);
        testSel[pos] = val;
        var available = hasAvailableVariant(variantList, testSel);

        $input.prop('disabled', !available);
        $input.attr('aria-disabled', available ? 'false' : 'true');
        $input.closest('.product__flavour-item').toggleClass('is-unavailable', !available);

        if (available) {
          if (firstAvailVal === null) firstAvailVal = val;
          if (currentSelections[pos] === val) currentIsAvailable = true;
        }
      });

      // Auto-switch if current selection became unavailable
      if (!currentIsAvailable && firstAvailVal !== null) {
        var $newInput = $('.product__flavour-item input[type="radio"][data-option-position="' + pos + '"]')
          .filter(function () { return $(this).val() === firstAvailVal; });
        $newInput.prop('checked', true);
        currentSelections[pos] = firstAvailVal;
      }
    }
  }

  // ─── Style 2 dynamic prices ──────────────────────────────────────────────────

  /**
   * For every Style 2 option card, find the variant that matches:
   *   - this card's own option value at its position
   *   - the currently selected values at all other positions
   *
   * Then update the card's price, compare-at, and per-unit display.
   *
   * @param {Array}  variantList
   * @param {Object} currentSelections
   * @param {number} numPositions
   */
  function updateStyle2Prices(variantList, currentSelections, numPositions) {
    $('.product__flavour-item--s2 input[type="radio"]').each(function () {
      var $input = $(this);
      var thisPos = parseInt($input.attr('data-option-position'), 10);
      var thisVal = $input.val();

      // Slot this card's own value into the current selection set
      var lookupSel = $.extend({}, currentSelections);
      lookupSel[thisPos] = thisVal;

      var variant = findVariant(variantList, lookupSel, numPositions);
      if (!variant) return;

      var $label = $input.next('label');

      // Main price
      // $label.find('.pf2__price').text(variant.priceFormatted);

      // Compare-at (show/hide)
      var $compare = $label.find('.pf2__compare');
      if (variant.compareAtRaw > variant.priceRaw) {
        if ($compare.length) {
          $compare.text(variant.compareAtFormatted).show();
        } else {
          $label.find('.pf2__price-row').prepend(
            '<del class="pf2__compare">' + variant.compareAtFormatted + '</del>'
          );
        }
      } else {
        $compare.hide();
      }

      // Per-unit price
      var $unit = $label.find('.pf2__unit');
      if (variant.units > 0 && variant.unitName) {
        // Integer division matches Shopify Liquid's `divided_by` filter
        var perUnitPence = Math.floor(variant.priceRaw / variant.units);
        $unit.text(formatMoney(perUnitPence) + ' per ' + variant.unitName).show();
      } else {
        $unit.hide();
      }
    });
  }

  // ─── ATC button sync ─────────────────────────────────────────────────────────

  /**
   * Update the sticky ATC compare-at <del> element.
   * The main <span> price is already handled by the existing matchVariant().
   */
  function syncStickyAtcCompareAt(variant) {
    if (!variant) return;
    var $wrap = $('.sticky-atc__price');
    var $del = $wrap.find('del');
    if (variant.compareAtRaw > variant.priceRaw) {
      if ($del.length) {
        $del.text(variant.compareAtFormatted).show();
      } else {
        $wrap.prepend('<del>' + variant.compareAtFormatted + '</del>');
      }
    } else {
      $del.hide();
    }
  }

  // ─── Master update ───────────────────────────────────────────────────────────

  /**
   * Central function called after every option change (and on page load).
   *
   * Order of operations:
   * 1. Build variant data from hidden select
   * 2. Cascade availability for downstream option positions
   * 3. Re-run the existing matchVariant() so the hidden select & ATC state
   *    reflect the (possibly auto-switched) selections
   * 4. Update Style 2 card prices
   * 5. Sync sticky ATC compare-at price
   */
  function variantSystemUpdate() {
    var variantList = buildVariantList();
    var positions = getPositions();
    var numPositions = positions.length;
    var currentSelections = getCurrentSelections();

    // Step 2: cascade availability (may mutate currentSelections via auto-switch)
    if (positions.length > 1) {
      cascadeAvailability(variantList, currentSelections, positions);
    }

    // Step 3: re-sync hidden select and ATC button via existing logic
    if (typeof matchVariant === 'function') matchVariant();
    if (typeof selectedVarint === 'function') selectedVarint();

    // Step 4: update Style 2 card prices after re-reading current state
    currentSelections = getCurrentSelections();
    if ($('.product__flavour-item--s2').length) {
      updateStyle2Prices(variantList, currentSelections, numPositions);
    }

    // Step 5: sync sticky ATC compare-at
    var selectedVariant = findVariant(variantList, currentSelections, numPositions);
    syncStickyAtcCompareAt(selectedVariant);

    // Step 6: update product gallery to selected variant's image
    // if (selectedVariant !== null && selectedVariant.imageIndex !== null && !isNaN(selectedVariant.imageIndex)) {
    //   var $gallery = $('.product__big-imgs');
    //   if ($gallery.length && $gallery.hasClass('slick-initialized')) {
    //     $gallery.slick('slickGoTo', selectedVariant.imageIndex);
    //   }
    // }
  }

  // ─── Event wiring ────────────────────────────────────────────────────────────

  // Use event delegation so this fires *after* the direct .change() handlers
  // already bound in product.js (matchVariant + selectedVarint + slide).
  $(document).on('change', '.product__flavour-item input[type="radio"]', function () {
    variantSystemUpdate();
  });

  // Initial run on DOM ready
  $(function () {
    if ($('.select__variant').length) {
      // Apply selected_by_default overrides unless a specific variant is in the URL
      if (!new URLSearchParams(window.location.search).has('variant')) {
        var appliedGroups = {};
        $('input[type="radio"][data-selected-by-default="true"]').each(function () {
          var name = this.name;
          if (!appliedGroups[name]) {
            appliedGroups[name] = true;
            $('input[type="radio"]').filter(function () { return this.name === name; }).prop('checked', false);
            $(this).prop('checked', true);
          }
        });
      }
      variantSystemUpdate();
    }
  });

}(jQuery));
